import React from 'react';
import { Button, Header, Icon, Label, List, Popup, Table } from 'semantic-ui-react';

import Acciones from './Componentes/Acciones';
import ModalIngresarPago, { ModalProps } from './ModalIngresarPago';
import { MetodosDePagoId, PagoConFechaDeRegistro } from './tipos';

type EstadoCuota =
    | 'POR_PAGAR'
    | 'PAGADA'
    | 'ATRASADA'
    | 'ABONADA'
    | 'ABONADA_ATRASADA'
    | 'FUTURA_CUOTA';

interface DatosCuota {
    numero: number;
    fechaDeVencimiento: string;
    montoInicial: number;
    estado: EstadoCuota;
    fechaDePago?: string;
    montoPagado?: number;
    deudaRestante?: number;
    interesPagado?: number;
    metodoDePago?: MetodosDePagoId;
    pagos?: Array<PagoConFechaDeRegistro>;
}

interface PagosMensualidades {
    idPlanDePago: string;
    cuotas: DatosCuota[];
    nombreCompletoApoderado: string;
    rutPasaporte: string;
    alRealizarPago(): void;
    alEditarPago(): void;
}

interface State {
    modalIngresarPagoAbierto: boolean;
    propsModal?: ModalProps;
}

class TablaPagosCuota extends React.Component<PagosMensualidades, State> {
    public state: State = {
        modalIngresarPagoAbierto: false,
    };
    public render() {
        const {
            idPlanDePago,
            cuotas,
            nombreCompletoApoderado,
            rutPasaporte,
            alRealizarPago,
            alEditarPago,
        } = this.props;
        return (
            <div>
                <Header as='h3'>Cuotas</Header>
                <Table>
                    <Table.Header>
                        <Table.Row textAlign='center'>
                            <Table.HeaderCell width='2'>Cuota </Table.HeaderCell>
                            <Table.HeaderCell width='2'>Fecha de vencimiento</Table.HeaderCell>
                            <Table.HeaderCell width='2'>Deuda</Table.HeaderCell>
                            <Table.HeaderCell width='2'>Pagado</Table.HeaderCell>
                            <Table.HeaderCell width='2'>Fecha de pago</Table.HeaderCell>
                            <Table.HeaderCell width='3'>Estado</Table.HeaderCell>
                            <Table.HeaderCell width='3' />
                            {/* Ingresar pago */}
                        </Table.Row>
                    </Table.Header>
                    <Table.Body>
                        {cuotas.map(cuota => (
                            <FilaCuota
                                key={cuota.numero}
                                pagos={cuota.pagos}
                                idPlanDePago={idPlanDePago}
                                alEditarPago={alEditarPago}
                                alPresionarIngresarPago={() => {
                                    this.setState({
                                        propsModal: {
                                            numero: cuota.numero,
                                            fechaDeVencimiento: cuota.fechaDeVencimiento,
                                            montoInicial: cuota.montoInicial,
                                            deudaRestante: cuota.deudaRestante,
                                            estado: cuota.estado,
                                            modalAbierto: true,
                                            idPlanDePago,
                                            nombreCompletoApoderado,
                                            rutPasaporte,
                                            alIntentarCerrar: this.cerrarModal,
                                            alRealizarPago: () => {
                                                alRealizarPago();
                                                this.cerrarModal();
                                            },
                                        },
                                    });
                                }}
                                {...cuota}
                            />
                        ))}
                    </Table.Body>
                </Table>
                {this.state.propsModal !== undefined ? (
                    <ModalIngresarPago {...this.state.propsModal} />
                ) : null}
            </div>
        );
    }

    public cerrarModal = () => {
        this.setState({ propsModal: undefined });
    };
}
interface FilaProps extends DatosCuota {
    idPlanDePago: string;
    alPresionarIngresarPago(): void;
    alEditarPago(): void;
}
const FilaCuota: React.FunctionComponent<FilaProps> = props => {
    const {
        numero,
        estado,
        fechaDeVencimiento,
        montoInicial,
        montoPagado,
        deudaRestante,
        fechaDePago,
        alPresionarIngresarPago,
        pagos,
        idPlanDePago,
        alEditarPago,
    } = props;

    return (
        <Table.Row key={numero} positive={estado === 'POR_PAGAR'}>
            <Table.Cell textAlign='center'>
                {numero}
                &nbsp;
                {dibujarMarcadorDeObservaciones(pagos)}
            </Table.Cell>

            <Table.Cell textAlign='center'>{escribirFecha(fechaDeVencimiento)}</Table.Cell>

            <Table.Cell textAlign='center'>
                {escribirMonto(deudaRestante !== undefined ? deudaRestante : montoInicial)}
            </Table.Cell>

            <Table.Cell textAlign='center'>
                {escribirMonto(montoPagado !== undefined ? montoPagado : 0)}
            </Table.Cell>

            <Table.Cell textAlign='center'>
                {fechaDePago ? escribirFecha(fechaDePago) : undefined}
            </Table.Cell>

            <Table.Cell verticalAlign='middle' textAlign='center'>
                {escribirEstadoCuota(estado, montoPagado)}
            </Table.Cell>

            <Table.Cell verticalAlign='middle' textAlign='right'>
                <div hidden={estado === 'FUTURA_CUOTA'}>
                    <Button
                        size='tiny'
                        content='Ingresar pago'
                        onClick={alPresionarIngresarPago}
                        disabled={estado === 'PAGADA'}
                    />
                    &nbsp;
                    <Acciones
                        numeroDeCuota={numero}
                        estadoCuota={estado}
                        pagos={pagos}
                        idPlanDePago={idPlanDePago}
                        alEditarPago={alEditarPago}
                    />
                </div>
            </Table.Cell>
        </Table.Row>
    );
};

const estadosPlanesDePago: Record<EstadoCuota, React.ReactNode> = {
    PAGADA: <Label color='green' content='Pagada' />,
    ABONADA: <Label color='yellow' content='Abonada' />,
    ATRASADA: <Label color='red' content='Atrasada' />,
    ABONADA_ATRASADA: <Label color='red' content='Atrasada (con abono)' />,
    POR_PAGAR: null,
    FUTURA_CUOTA: null,
};

function escribirEstadoCuota(estado: EstadoCuota, _montoPagado?: number) {
    return estadosPlanesDePago[estado];
}

function dibujarMarcadorDeObservaciones(pagos?: ReadonlyArray<PagoConFechaDeRegistro>) {
    const vacio = <div style={{ width: '15px', display: 'inline-block' }} />;
    if (!pagos) return vacio;
    const pagosConObservaciones = pagos.filter(pago => pago.observaciones !== '');
    if (pagosConObservaciones.length === 0) return vacio;

    return (
        <Popup trigger={<Icon name='comments' />} wide>
            <Popup.Header content='Observaciones de los pagos' />
            <List celled>
                {pagosConObservaciones.map((pago, i) => (
                    <List.Item key={i}>
                        <List.Content>
                            <List.Header>
                                Pago {i + 1} del {escribirFecha(pago.fecha)}
                            </List.Header>
                            {pago.observaciones}
                        </List.Content>
                    </List.Item>
                ))}
            </List>
        </Popup>
    );
}

// TODO: Estas funciones se usan en muchos lugares. Seleccionar exactamente donde deberían estar
// para ser usadas por el cliente, mas allá de solo cobranzas

function escribirFecha(fecha: string) {
    const [año, mes, dia] = fecha.split('-');
    return `${dia}-${mes}-${año}`;
}

function escribirMonto(monto: number) {
    const valorConPuntos = monto.toString().replace(/\B(?=(\d{3})+(?!\d))/g, '.');
    return '$' + valorConPuntos;
}

export default TablaPagosCuota;



// WEBPACK FOOTER //
// ./src/modulos/Cobranzas/componentes/FichaDeApoderado/TablaPagosCuota.tsx